/**
 * 토지이용계획 섹션 (NDA 동의 후 표시)
 * - 리스팅의 기본 정보 (지목, 용도지역) 표시
 * - 토지이용규제 API 프록시 호출 (PNU 필요)
 * - PNU 없을 경우 외부 포털 링크 제공
 */
import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../../utils/api";

interface Regulation {
  code: string;
  name: string;
}

interface Props {
  listingId: string;
  landCategory: string;   // 지목
  zoning: string;         // 용도지역
  exactAddress: string;   // 정확한 주소 (NDA 동의 후)
  isMobile: boolean;
}

/** 정확한 주소에서 PNU 구성에 필요한 번지 정보 파싱 시도 */
function parseLotFromAddress(address: string): { main: number; sub: number; isMountain: boolean } | null {
  // 예: "경상북도 영천시 금호읍 상리 123-4" 또는 "... 산123-4"
  const m = address.match(/(?:^|\s)(산)?(\d+)(?:-(\d+))?(?:\s*$|\s+)/);
  if (!m) return null;
  return {
    isMountain: m[1] === "산",
    main: parseInt(m[2]) || 0,
    sub: parseInt(m[3] || "0") || 0,
  };
}

export function LandUseSection({ landCategory, zoning, exactAddress, isMobile }: Props) {
  const [regulations, setRegulations] = useState<Regulation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pnuHint, setPnuHint] = useState("");

  // PNU 없이는 API 조회 불가 — 주소 파싱 힌트만 표시
  useEffect(() => {
    const lot = parseLotFromAddress(exactAddress);
    if (lot) {
      const lotStr = `${lot.isMountain ? "산" : ""}${lot.main}${lot.sub ? `-${lot.sub}` : ""}`;
      setPnuHint(lotStr);
    }
  }, [exactAddress]);

  const fetchRegulation = useCallback(async (pnu: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/land/use-regulation?pnu=${pnu}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { regulations: Regulation[]; error: string | null };
      if (json.error) throw new Error(json.error);
      setRegulations(json.regulations);
    } catch (e: any) {
      setError(e.message || "조회 실패");
    } finally {
      setLoading(false);
    }
  }, []);

  // luris.molit.go.kr 검색 링크 (주소 기반)
  const lurisUrl = `https://cloud.luris.go.kr/web/index.jsp`;

  return (
    <div style={st.section}>
      <div style={st.header}>
        <h3 style={st.title}>{"🗺️ 토지이용계획"}</h3>
        <span style={{ ...st.badge, background: "rgba(16,185,129,0.15)", color: "#10B981" }}>
          {"NDA 열람"}
        </span>
      </div>

      {/* 기본 정보 (리스팅 데이터) */}
      <div style={{ ...st.grid, gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)" }}>
        <InfoCard label="지목" value={landCategory || "-"} icon={"🏷️"} />
        <InfoCard label="용도지역" value={zoning || "-"} icon={"📋"} />
        <InfoCard label="소재지" value={exactAddress} icon={"📍"} small />
      </div>

      {/* 토지이용규제 API 결과 */}
      {regulations.length > 0 && (
        <div style={st.regWrap}>
          <div style={st.regTitle}>{"용도지역·지구·구역"}</div>
          <div style={st.regList}>
            {regulations.map((r, i) => (
              <span key={i} style={st.regTag}>
                {r.name}
                {r.code && <span style={st.regCode}>{r.code}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div style={st.loadRow}>
          <div style={st.spinner} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{"조회 중..."}</span>
        </div>
      )}

      {error && (
        <div style={st.errorMsg}>{"⚠️ " + error}</div>
      )}

      {/* 외부 포털 안내 */}
      <div style={st.portalWrap}>
        <div style={st.portalInfo}>
          {"PNU(필지번호)가 있으면 상세 규제 조회가 가능합니다."}
          {pnuHint && ` (파싱된 번지: ${pnuHint})`}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
          <a
            href={lurisUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={st.linkBtn}
          >
            {"🔗 토지이음 열람"}
          </a>
          <a
            href="https://www.eum.go.kr/web/am/amMain.jsp"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...st.linkBtn, background: "transparent", border: "1px solid var(--border-medium)", color: "var(--text-secondary)" }}
          >
            {"🔗 토지이음 포털"}
          </a>
        </div>
        {/* PNU 수동 입력 */}
        <PnuInput onSearch={fetchRegulation} />
      </div>
    </div>
  );
}

// ── PNU 수동 입력 컴포넌트 ──────────────────────
function PnuInput({ onSearch }: { onSearch: (pnu: string) => void }) {
  const [pnu, setPnu] = useState("");
  return (
    <div style={st.pnuWrap}>
      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{"PNU 직접 조회:"}</span>
      <input
        type="text"
        placeholder="19자리 PNU 입력"
        value={pnu}
        onChange={(e) => setPnu(e.target.value.replace(/\D/g, "").slice(0, 19))}
        style={st.pnuInput}
        maxLength={19}
      />
      <button
        style={{
          ...st.pnuBtn,
          opacity: pnu.length === 19 ? 1 : 0.4,
          cursor: pnu.length === 19 ? "pointer" : "default",
        }}
        disabled={pnu.length !== 19}
        onClick={() => onSearch(pnu)}
      >
        {"조회"}
      </button>
    </div>
  );
}

// ── InfoCard 서브 컴포넌트 ──────────────────────
function InfoCard({ label, value, icon, small }: {
  label: string; value: string; icon: string; small?: boolean;
}) {
  return (
    <div style={st.infoCard}>
      <div style={st.infoIcon}>{icon}</div>
      <div style={st.infoLabel}>{label}</div>
      <div style={{ ...st.infoValue, fontSize: small ? 11 : 14 }}>{value}</div>
    </div>
  );
}

// ── 스타일 ──────────────────────────────────────
const gold = "#D4A853";
const st: Record<string, React.CSSProperties> = {
  section: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderRadius: 12,
    padding: "20px 16px",
    marginBottom: 12,
  },
  header: {
    display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
  },
  title: {
    fontSize: 16, fontWeight: 700, color: "var(--text-primary)", margin: 0,
  },
  badge: {
    padding: "2px 8px", borderRadius: 20,
    fontSize: 12, fontWeight: 600,
  },
  grid: {
    display: "grid", gap: 10, marginBottom: 16,
  },
  infoCard: {
    background: "var(--bg-secondary)", borderRadius: 10,
    padding: "12px 14px",
  },
  infoIcon: { fontSize: 18, marginBottom: 6 },
  infoLabel: { fontSize: 11, color: "var(--text-muted)", marginBottom: 4 },
  infoValue: {
    fontWeight: 600, color: "var(--text-primary)",
    wordBreak: "break-word" as const, lineHeight: "1.4",
  },
  regWrap: {
    background: "var(--bg-secondary)", borderRadius: 10,
    padding: "12px 14px", marginBottom: 14,
  },
  regTitle: {
    fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 8,
  },
  regList: { display: "flex", flexWrap: "wrap" as const, gap: 6 },
  regTag: {
    padding: "4px 10px", borderRadius: 20,
    background: `rgba(212,168,83,0.12)`, color: gold,
    fontSize: 12, fontWeight: 600,
    display: "flex", alignItems: "center", gap: 4,
  },
  regCode: {
    padding: "1px 5px", borderRadius: 4,
    background: "rgba(0,0,0,0.15)", color: "rgba(212,168,83,0.7)",
    fontSize: 10,
  },
  loadRow: {
    display: "flex", alignItems: "center", gap: 8, padding: "8px 0",
  },
  spinner: {
    width: 18, height: 18, borderRadius: "50%",
    border: "2px solid var(--border-light)",
    borderTopColor: gold,
    animation: "spin 0.8s linear infinite",
  },
  errorMsg: {
    fontSize: 12, color: "var(--lt-red)", padding: "6px 0",
  },
  portalWrap: {
    borderTop: "1px solid var(--border-light)",
    paddingTop: 14, display: "flex", flexDirection: "column" as const, gap: 10,
  },
  portalInfo: {
    fontSize: 12, color: "var(--text-muted)", lineHeight: "1.5",
  },
  linkBtn: {
    padding: "8px 14px", borderRadius: 8,
    background: `rgba(212,168,83,0.12)`, color: gold,
    fontSize: 13, fontWeight: 600, textDecoration: "none",
    border: `1px solid rgba(212,168,83,0.3)`,
    display: "inline-block",
  },
  pnuWrap: {
    display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" as const,
  },
  pnuInput: {
    flex: 1, minWidth: 140, padding: "7px 10px",
    borderRadius: 8, border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)", color: "var(--text-primary)",
    fontSize: 13, outline: "none",
    fontFamily: "monospace",
  },
  pnuBtn: {
    padding: "7px 14px", borderRadius: 8,
    border: "none", background: gold, color: "#0F172A",
    fontSize: 13, fontWeight: 700,
    transition: "opacity 0.15s",
  },
};
